import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlus, FaEdit, FaTrash, FaMapMarkerAlt, FaSave, FaTimes, 
  FaEye, FaEyeSlash, FaSearch, FaStar 
} from 'react-icons/fa';
import { useLocationsData, LocationCategory, Location } from '../hooks/useLocationsData';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

// Iconos predefinidos para categorías
const CATEGORY_ICONS = [
  '🏠', '❤️', '🍽️', '🏨', '🎭', '🛍️', '🏖️', '🌮', '☕', '🎵', 
  '📚', '💒', '🎊', '✈️', '🚗', '🌳', '🏔️', '🎯', '🎨', '⚽'
];

const CATEGORY_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981', 
  '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899'
];

export const LocationsManager: React.FC = () => {
  const {
    categories,
    locations,
    loading,
    addCategory,
    updateCategory,
    deleteCategory,
    addLocation,
    updateLocation,
    deleteLocation,
    getLocationsByCategory,
    getStats
  } = useLocationsData();

  const { geocodeAddress } = useGoogleMaps();

  // Estados para formularios
  const [activeTab, setActiveTab] = useState<'categories' | 'locations'>('categories');
  const [editingCategory, setEditingCategory] = useState<LocationCategory | null>(null);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');

  // Estados de formularios
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: '📍',
    color: '#3b82f6',
    order: 0
  });

  const [locationForm, setLocationForm] = useState({
    name: '',
    description: '',
    categoryId: '',
    address: '',
    lat: 0,
    lng: 0,
    dateVisited: '',
    rating: 0,
    notes: '',
    isVisible: true
  });

  const [isGeocoding, setIsGeocoding] = useState(false);

  const stats = getStats();

  // Handlers para categorías
  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryForm);
        setEditingCategory(null);
      } else {
        await addCategory(categoryForm);
      }
      resetCategoryForm();
      setShowCategoryForm(false);
    } catch (error) {
      alert('Error guardando categoría: ' + (error as Error).message);
    }
  };

  const handleCategoryEdit = (category: LocationCategory) => {
    setCategoryForm({
      name: category.name,
      description: category.description,
      icon: category.icon,
      color: category.color,
      order: category.order
    });
    setEditingCategory(category);
    setShowCategoryForm(true);
  };

  const handleCategoryDelete = async (id: string) => {
    if (window.confirm('¿Seguro que quieres eliminar esta categoría?')) {
      try {
        await deleteCategory(id);
      } catch (error) {
        alert((error as Error).message);
      }
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      icon: '📍',
      color: '#3b82f6',
      order: categories.length
    });
    setEditingCategory(null);
  };

  // Handlers para ubicaciones
  const handleLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await updateLocation(editingLocation.id, locationForm);
        setEditingLocation(null);
      } else {
        await addLocation(locationForm);
      }
      resetLocationForm();
      setShowLocationForm(false);
    } catch (error) {
      alert('Error guardando ubicación: ' + (error as Error).message);
    }
  };

  const handleLocationEdit = (location: Location) => {
    setLocationForm({
      name: location.name,
      description: location.description,
      categoryId: location.categoryId,
      address: location.address || '',
      lat: location.lat,
      lng: location.lng,
      dateVisited: location.dateVisited || '',
      rating: location.rating || 0,
      notes: location.notes || '',
      isVisible: location.isVisible
    });
    setEditingLocation(location);
    setShowLocationForm(true);
  };

  const handleLocationDelete = async (id: string) => {
    if (window.confirm('¿Seguro que quieres eliminar esta ubicación?')) {
      try {
        await deleteLocation(id);
      } catch (error) {
        alert('Error eliminando ubicación: ' + (error as Error).message);
      }
    }
  };

  const resetLocationForm = () => {
    setLocationForm({
      name: '',
      description: '',
      categoryId: '',
      address: '',
      lat: 0,
      lng: 0,
      dateVisited: '',
      rating: 0,
      notes: '',
      isVisible: true
    });
    setEditingLocation(null);
  };

  // Geocodificación de dirección
  const handleGeocodeAddress = async () => {
    if (!locationForm.address.trim()) {
      alert('Ingresa una dirección para buscar coordenadas');
      return;
    }

    setIsGeocoding(true);
    try {
      const coords = await geocodeAddress(locationForm.address);
      if (coords) {
        setLocationForm(prev => ({
          ...prev,
          lat: coords.lat,
          lng: coords.lng
        }));
        alert('¡Coordenadas encontradas!');
      } else {
        alert('No se pudieron encontrar las coordenadas para esta dirección');
      }
    } catch (error) {
      alert('Error buscando coordenadas');
    } finally {
      setIsGeocoding(false);
    }
  };

  // Filtrado de ubicaciones
  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategoryFilter === 'all' || location.categoryId === selectedCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/40 p-4 rounded-xl">
          <div className="text-2xl font-bold text-primary">{stats.totalCategories}</div>
          <div className="text-sm text-text-light">Categorías</div>
        </div>
        <div className="bg-white/40 p-4 rounded-xl">
          <div className="text-2xl font-bold text-secondary">{stats.totalLocations}</div>
          <div className="text-sm text-text-light">Ubicaciones</div>
        </div>
        <div className="bg-white/40 p-4 rounded-xl">
          <div className="text-2xl font-bold text-green-500">{stats.categoriesWithLocations}</div>
          <div className="text-sm text-text-light">Con ubicaciones</div>
        </div>
        <div className="bg-white/40 p-4 rounded-xl">
          <div className="text-2xl font-bold text-orange-500">{stats.averageLocationsPerCategory}</div>
          <div className="text-sm text-text-light">Promedio por categoría</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-white/20">
        <button
          onClick={() => setActiveTab('categories')}
          className={`pb-2 px-4 font-semibold transition-colors ${
            activeTab === 'categories' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-text-light hover:text-text-main'
          }`}
        >
          📂 Categorías ({categories.length})
        </button>
        <button
          onClick={() => setActiveTab('locations')}
          className={`pb-2 px-4 font-semibold transition-colors ${
            activeTab === 'locations' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-text-light hover:text-text-main'
          }`}
        >
          📍 Ubicaciones ({locations.length})
        </button>
      </div>

      {/* Contenido de Categorías */}
      {activeTab === 'categories' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-text-main">Gestión de Categorías</h3>
            <button
              onClick={() => setShowCategoryForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
            >
              <FaPlus /> Nueva Categoría
            </button>
          </div>

          {/* Lista de categorías */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/40 p-4 rounded-xl"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl" style={{ color: category.color }}>
                      {category.icon}
                    </span>
                    <div>
                      <h4 className="font-bold text-text-main">{category.name}</h4>
                      <p className="text-sm text-text-light">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCategoryEdit(category)}
                      className="p-2 text-blue-500 hover:bg-blue-500/20 rounded-lg transition-colors"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleCategoryDelete(category.id)}
                      className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-text-light">
                  {getLocationsByCategory(category.id).length} ubicaciones
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Contenido de Ubicaciones */}
      {activeTab === 'locations' && (
        <div className="space-y-6">
          {/* Header con filtros */}
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="flex gap-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-light" />
                <input
                  type="text"
                  placeholder="Buscar ubicaciones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-white/20 border border-white/30 rounded-lg text-text-main placeholder-text-light"
                />
              </div>
              <select
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                className="px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-text-main"
              >
                <option value="all">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setShowLocationForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-primary rounded-lg hover:bg-opacity-80 transition-colors"
            >
              <FaMapMarkerAlt /> Nueva Ubicación
            </button>
          </div>

          {/* Lista de ubicaciones */}
          <div className="space-y-3">
            {filteredLocations.map((location) => {
              const category = categories.find(cat => cat.id === location.categoryId);
              return (
                <motion.div
                  key={location.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/40 p-4 rounded-xl flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl" style={{ color: category?.color }}>
                      {category?.icon}
                    </span>
                    <div>
                      <h4 className="font-bold text-text-main flex items-center gap-2">
                        {location.name}
                        {!location.isVisible && <FaEyeSlash className="text-gray-400" />}
                      </h4>
                      <p className="text-sm text-text-light">{location.description}</p>
                      {location.address && (
                        <p className="text-xs text-text-light">📍 {location.address}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1">
                        {location.rating && (
                          <div className="flex items-center gap-1">
                            {Array.from({ length: 5 }, (_, i) => (
                              <FaStar
                                key={i}
                                className={`w-3 h-3 ${i < location.rating! ? 'text-yellow-400' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        )}
                        {location.dateVisited && (
                          <span className="text-xs text-text-light">
                            📅 {new Date(location.dateVisited).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => 
                        updateLocation(location.id, { isVisible: !location.isVisible })
                      }
                      className={`p-2 rounded-lg transition-colors ${
                        location.isVisible 
                          ? 'text-green-500 hover:bg-green-500/20' 
                          : 'text-gray-400 hover:bg-gray-400/20'
                      }`}
                    >
                      {location.isVisible ? <FaEye /> : <FaEyeSlash />}
                    </button>
                    <button
                      onClick={() => handleLocationEdit(location)}
                      className="p-2 text-blue-500 hover:bg-blue-500/20 rounded-lg transition-colors"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleLocationDelete(location.id)}
                      className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal para Categoría */}
      <AnimatePresence>
        {showCategoryForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
                </h3>
                <button
                  onClick={() => {
                    setShowCategoryForm(false);
                    resetCategoryForm();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Icono
                    </label>
                    <div className="grid grid-cols-5 gap-2 p-3 border border-gray-300 rounded-lg max-h-32 overflow-y-auto">
                      {CATEGORY_ICONS.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setCategoryForm(prev => ({ ...prev, icon }))}
                          className={`p-2 text-xl rounded hover:bg-gray-100 ${
                            categoryForm.icon === icon ? 'bg-blue-100 ring-2 ring-blue-500' : ''
                          }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <div className="grid grid-cols-3 gap-2 p-3 border border-gray-300 rounded-lg">
                      {CATEGORY_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setCategoryForm(prev => ({ ...prev, color }))}
                          className={`w-8 h-8 rounded-full border-2 ${
                            categoryForm.color === color ? 'border-gray-800' : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCategoryForm(false);
                      resetCategoryForm();
                    }}
                    className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-hover flex items-center justify-center gap-2"
                  >
                    <FaSave /> {editingCategory ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal para Ubicación */}
      <AnimatePresence>
        {showLocationForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingLocation ? 'Editar Ubicación' : 'Nueva Ubicación'}
                </h3>
                <button
                  onClick={() => {
                    setShowLocationForm(false);
                    resetLocationForm();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleLocationSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={locationForm.name}
                      onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoría *
                    </label>
                    <select
                      value={locationForm.categoryId}
                      onChange={(e) => setLocationForm(prev => ({ ...prev, categoryId: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    >
                      <option value="">Seleccionar categoría</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.icon} {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={locationForm.description}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={locationForm.address}
                      onChange={(e) => setLocationForm(prev => ({ ...prev, address: e.target.value }))}
                      className="flex-1 p-3 border border-gray-300 rounded-lg"
                      placeholder="Ej: Centro de Monterrey, NL"
                    />
                    <button
                      type="button"
                      onClick={handleGeocodeAddress}
                      disabled={isGeocoding || !locationForm.address.trim()}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                    >
                      {isGeocoding ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      ) : (
                        <FaSearch />
                      )}
                      Buscar
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Latitud *
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={locationForm.lat}
                      onChange={(e) => setLocationForm(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Longitud *
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={locationForm.lng}
                      onChange={(e) => setLocationForm(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha visitada
                    </label>
                    <input
                      type="date"
                      value={locationForm.dateVisited}
                      onChange={(e) => setLocationForm(prev => ({ ...prev, dateVisited: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Calificación
                    </label>
                    <select
                      value={locationForm.rating}
                      onChange={(e) => setLocationForm(prev => ({ ...prev, rating: parseInt(e.target.value) }))}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    >
                      <option value={0}>Sin calificar</option>
                      {[1, 2, 3, 4, 5].map(rating => (
                        <option key={rating} value={rating}>
                          {'⭐'.repeat(rating)} ({rating}/5)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas
                  </label>
                  <textarea
                    value={locationForm.notes}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    rows={3}
                    placeholder="Notas especiales, recuerdos, recomendaciones..."
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isVisible"
                    checked={locationForm.isVisible}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, isVisible: e.target.checked }))}
                    className="w-4 h-4 text-primary"
                  />
                  <label htmlFor="isVisible" className="text-sm font-medium text-gray-700">
                    Visible en el mapa público
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowLocationForm(false);
                      resetLocationForm();
                    }}
                    className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-secondary text-primary rounded-lg hover:bg-opacity-80 flex items-center justify-center gap-2"
                  >
                    <FaSave /> {editingLocation ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};